import Recorder from '../components/Recorder';
import { useState, useRef, useEffect, useCallback } from 'react'
import { Alert, Button, IconButton, Tooltip, Typography, Switch } from '@mui/material';
import Stack from '@mui/material/Stack';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckIcon from '@mui/icons-material/Check';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../Services/Api';
import * as Sentry from "@sentry/browser";
import FeedbackIcon from '@mui/icons-material/Feedback';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import ProcessingComponent from '../designsystem/ProcessingComponent';
import Loading from '../components/Loading';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../Services/i18n';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BugReportOutlined from '@mui/icons-material/BugReportOutlined';
import { glassCard, ACCENT, STATUS_WON, STATUS_LOST, TEXT_PRIMARY, TEXT_SECONDARY, noMotion } from '../designsystem/tokens';
import SuccessOverlay from './components/SuccessOverlay';
import DraftBanner, { getDraft, clearDraft } from './components/DraftBanner';
import LiveTranscript from './components/LiveTranscript';

const IS_DEV = process.env.NODE_ENV?.trim() === 'development';

function Labia() {
  const { t } = useTranslation();
  const { setHide } = useOutletContext();
  const { state } = useLocation();
  const { text, example, createdEntities: navCreatedEntities } = state || {};
  const [loading, setLoading] = useState(true);
  const crmUpdated = state != undefined ? state.ok : false
  const failed = state != undefined ? state.fail : false
  const message = state != undefined ? state.message : undefined
  const navigate = useNavigate();
  var clear = false
  const abortControllerRef = useRef(null);
  const errorCardRef = useRef(null);
  const successTimerRef = useRef(null);

  // Phase 1.3: Success overlay
  const [createdEntities, setCreatedEntities] = useState(navCreatedEntities || []);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(crmUpdated && !failed && navCreatedEntities?.length > 0);

  // Phase 2.3: Draft auto-save
  const [draft, setDraft] = useState(null);

  // Phase 3.1: Live transcription
  const [transcriptText, setTranscriptText] = useState('');
  const [transcriptConfirmed, setTranscriptConfirmed] = useState(false);

  // Express mode
  const [expressMode, setExpressMode] = useState(
    () => localStorage.getItem('labia_express_mode') === 'true'
  );
  const toggleExpressMode = () => {
    setExpressMode(prev => {
      localStorage.setItem('labia_express_mode', String(!prev));
      return !prev;
    });
  };

  function setClear(val) {
    clear = val
  }

  const [process, setProcess] = useState(false)
  const [error, setError] = useState(false)
  const [success, setSuccess] = useState(crmUpdated && !failed)
  const [processingText, setProcessingText] = useState(t('processingAudio'))
  const [recording, setRecording] = useState(false)
  var lastText = useRef("")
  const [errorAlertMessage, setErrorAlertMessage] = useState(message)
  const [errorAlertOpen, setErrorAlertOpen] = useState(failed);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const idleTimerRef = useRef(null);

  // Auto-dismiss success alert after 3 seconds (only when no overlay)
  useEffect(() => {
    if (success && !showSuccessOverlay) {
      successTimerRef.current = setTimeout(() => {
        setSuccess(false);
      }, 3000);
    }
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, [success, showSuccessOverlay]);

  // Focus error card when error appears
  useEffect(() => {
    if (error && errorCardRef.current) {
      errorCardRef.current.focus();
    }
  }, [error]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const lang = query.get('lang');
    if (lang) {
      i18n.changeLanguage(lang);
    }
    if (text != undefined) {
      lastText.current = text
      setError(true)
    }
    // Check for saved draft
    const savedDraft = getDraft();
    if (savedDraft) {
      setDraft(savedDraft);
    }
    api.getUserData().then((response) => {
      setLoading(false)
    }).catch((error) => {
      navigate("/")
      setLoading(false)
    })
  }, [text]);

  // Idle timer: show help tooltip after 8s of inactivity
  useEffect(() => {
    if (!recording && !process) {
      idleTimerRef.current = setTimeout(() => setShowHelpTooltip(true), 8000);
    } else {
      setShowHelpTooltip(false);
    }
    return () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current); };
  }, [recording, process]);

  const goToOnboarding = () => {
    setShowHelpTooltip(false);
    navigate("/app/onboarding");
  };

  const sendAudio = (audio) => {
    setClear(false)
    setProcess(true)
    setHide(true)
    const controller = new AbortController();
    abortControllerRef.current = controller;
    api.UploadAudio(audio, onUploadProgress, controller.signal).then((response) => {
      abortControllerRef.current = null;
      navigate("/app/validate", { state: { text: response.text, oneShot: expressMode } })
    }).catch((error) => {
      abortControllerRef.current = null;
      if (error?.cancelled) {
        setProcess(false);
        setHide(false);
        return;
      }
      Sentry.captureException(error);
      setError(true)
      setProcess(false)
      setHide(false)
    })
  }

  const cancelProcessing = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setProcess(false);
    setHide(false);
    setProcessingText(t('processingAudio'));
  }, [setHide, t]);

  const reject = (error) => {
    console.log(error)
    Sentry.captureException(error);
    setError(true)
    setProcess(false)
  }

  const onUploadProgress = (progressEvent) => {
    var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
    console.log(percentCompleted)
    if (percentCompleted == 100) {
      setProcessingText(t('processingAudio'))
    } else {
      setProcessingText(t('processingAudio') + " " + percentCompleted + "%")
    }
  }

  return (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="center"
      sx={{ height: 'calc(100dvh - 112px)', minHeight: 'calc(100vh - 112px)', overflow: 'hidden' }}
    >
      {/* Help icon with idle-triggered tooltip */}
      {!example &&
        <Tooltip
          title={t('discoverLabia')}
          open={showHelpTooltip}
          arrow
          placement="bottom-end"
          onClose={() => setShowHelpTooltip(false)}
        >
          <IconButton
            data-testid="onboarding-link"
            aria-label={t('learnToUseLabia')}
            onClick={goToOnboarding}
            sx={{
              position: "absolute",
              top: "max(env(safe-area-inset-top, 0px), 1rem)",
              right: "1rem",
              color: TEXT_SECONDARY,
              opacity: 0.7,
              '&:hover': { opacity: 1 },
              borderRadius: '50%',
            }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
      }
      {IS_DEV && !example &&
        <IconButton
          data-testid="dev-test-link"
          aria-label="Dev test scenarios"
          onClick={() => navigate("/app/dev-test")}
          sx={{
            position: "absolute",
            top: "max(env(safe-area-inset-top, 0px), 1rem)",
            left: "1rem",
            color: TEXT_SECONDARY,
            opacity: 0.5,
            '&:hover': { opacity: 1 },
          }}
        >
          <BugReportOutlined />
        </IconButton>
      }
      {example &&
        <IconButton
          onClick={() => { navigate("/app") }}
          sx={{
            color: TEXT_PRIMARY,
            position: "absolute",
            top: "max(env(safe-area-inset-top, 0px), 1rem)",
            left: "1rem"
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 35 }} />
        </IconButton>
      }
      <Loading loading={loading} />

      {/* Success overlay with created entities */}
      {showSuccessOverlay && createdEntities.length > 0 && (
        <SuccessOverlay
          createdEntities={createdEntities}
          onRecordAnother={() => {
            setShowSuccessOverlay(false);
            setSuccess(false);
            setCreatedEntities([]);
          }}
          onDone={() => {
            setShowSuccessOverlay(false);
            setSuccess(false);
            setCreatedEntities([]);
          }}
        />
      )}

      {/* Fallback success alert when no entity details */}
      {success && !showSuccessOverlay &&
        <Alert
          data-testid="success-alert"
          role="status"
          aria-live="polite"
          icon={<CheckIcon sx={{ color: "white" }} fontSize="inherit" />}
          sx={{
            backgroundColor: STATUS_WON,
            color: "white",
            position: "fixed",
            width: "90%",
            left: "5%",
            right: "5%",
            bottom: 'calc(104px + 16px)',
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            borderRadius: '12px',
            ...noMotion,
          }}
        >
          {t('audioSentCorrectly')}
        </Alert>
      }

      {/* Draft banner */}
      {draft && !recording && !process && !error && !success && (
        <DraftBanner
          draft={draft}
          onResume={() => {
            setDraft(null);
            navigate("/app/validate", { state: { text: draft.text, json: draft.json } });
          }}
          onDiscard={() => {
            clearDraft();
            setDraft(null);
          }}
        />
      )}

      {/* Error alert from navigation state */}
      {errorAlertOpen &&
        <Alert
          severity="error"
          sx={{
            position: "fixed",
            width: "90%",
            left: "5%",
            right: "5%",
            top: "max(env(safe-area-inset-top, 0px), 5%)",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            borderRadius: '12px',
          }}
        >
          {errorAlertMessage}
        </Alert>
      }

      {/* Recorder */}
      {!process &&
        <Stack direction="column" justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
          <Recorder
            example={example}
            small={true}
            finished={sendAudio}
            enableLiveTranscription={true}
            onTranscript={(text, confirmed) => {
              setTranscriptText(text);
              setTranscriptConfirmed(confirmed);
            }}
            onClear={() => {
              setClear(true)
              setHide(false)
              setTranscriptText('')
            }}
            onStopRecording={() => {
              setRecording(false)
              setHide(false)
            }}
            onStart={() => {
              setSuccess(false)
              setError(false)
              setRecording(true)
              setHide(true)
              setShowSuccessOverlay(false)
              setTranscriptText('')
              setDraft(null)
            }}
          />

          {/* Live transcript during recording */}
          {recording && transcriptText && (
            <LiveTranscript text={transcriptText} isConfirmed={transcriptConfirmed} />
          )}

          {/* Express mode toggle */}
          {!recording && !process && !error && !success && !example && !draft && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                position: 'fixed',
                bottom: 'calc(104px + 24px)',
                ...glassCard,
                p: '8px 16px',
                borderRadius: '20px',
              }}
            >
              <BoltIcon sx={{ color: ACCENT, fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: TEXT_PRIMARY, fontSize: '0.82rem' }}>
                {t('expressMode')}
              </Typography>
              <Switch
                data-testid="express-mode-toggle"
                checked={expressMode}
                onChange={toggleExpressMode}
                size="small"
                aria-label={t('expressDescription')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT },
                }}
              />
            </Stack>
          )}
        </Stack>
      }

      {/* Processing state with cancel button */}
      {process &&
        <Stack spacing={2} alignItems="center">
          <ProcessingComponent processingText={processingText} />
          <Button
            data-testid="cancel-processing"
            variant="outlined"
            onClick={cancelProcessing}
            sx={{
              color: TEXT_PRIMARY,
              borderColor: 'rgba(255,255,255,0.3)',
              borderRadius: '12px',
              mt: 2,
              '&:hover': { borderColor: 'rgba(255,255,255,0.5)' },
            }}
          >
            {t('cancel')}
          </Button>
        </Stack>
      }

      {/* Consolidated error card with retry + re-record */}
      {error &&
        <Stack
          ref={errorCardRef}
          tabIndex={-1}
          data-testid="error-alert"
          role="alert"
          aria-live="assertive"
          spacing={1.5}
          alignItems="center"
          sx={{
            ...glassCard,
            position: 'fixed',
            bottom: 'calc(104px + 24px)',
            left: '5%',
            right: '5%',
            width: '90%',
            textAlign: 'center',
            border: `1px solid ${STATUS_LOST}40`,
            backgroundColor: 'rgba(229,115,115,0.08)',
          }}
        >
          <IconButton
            size="small"
            onClick={() => setError(false)}
            sx={{ position: 'absolute', top: 8, right: 8, color: TEXT_PRIMARY }}
            aria-label={t('close')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Stack direction="row" spacing={1} alignItems="center">
            <FeedbackIcon sx={{ color: STATUS_LOST }} fontSize="small" />
            <Typography sx={{ color: STATUS_LOST, fontWeight: 500 }}>
              {t('errorSendingAudio')}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            {lastText.current !== "" &&
              <Button
                data-testid="retry-button"
                variant="contained"
                size="small"
                startIcon={<ReplayIcon />}
                onClick={() => {
                  setError(false);
                  navigate("/app/validate", { state: { text: lastText.current } })
                }}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  backgroundColor: ACCENT,
                  '&:hover': { backgroundColor: '#5e9294' },
                }}
              >
                {t('retry')}
              </Button>
            }
            <Button
              data-testid="record-again-button"
              variant="outlined"
              size="small"
              onClick={() => {
                setError(false);
                lastText.current = "";
              }}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                color: TEXT_PRIMARY,
                borderColor: 'rgba(255,255,255,0.3)',
              }}
            >
              {t('recordAgain')}
            </Button>
          </Stack>
        </Stack>
      }
    </Stack>
  );
}

export default Labia;
